import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { 
  RefreshCw, 
  TrendingUp, 
  Clock, 
  DollarSign, 
  Euro,
  AlertTriangle,
  Check,
  Settings
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = window.location.origin || '';

const AdminFxRatePage = () => {
  const [fxData, setFxData] = useState(null);
  const [settings, setSettings] = useState({
    auto_update_enabled: false,
    update_interval_hours: 24,
    fallback_rate: 0.027,
    min_rate: 0.020,
    max_rate: 0.040,
  });
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [manualRate, setManualRate] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  const fetchFxData = useCallback(async () => {
    try {
      const token = localStorage.getItem('vipgez_admin_token');
      
      // Fetch FX rate and scheduler status in parallel
      const [rateResponse, statusResponse] = await Promise.all([
        fetch(`${API_BASE}/api/admin/fx-rate`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE}/api/admin/fx-rate/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      const data = await rateResponse.json();
      const statusData = await statusResponse.json();
      
      if (data.success) {
        setFxData(data.fxRate);
        if (data.settings) {
          setSettings(data.settings);
        }
        setManualRate(data.fxRate?.rate?.toFixed(6) || '0.027');
      }
      
      if (statusData.success) {
        setSchedulerStatus(statusData.status);
      }
    } catch (error) {
      console.error('Failed to fetch FX rate:', error);
      toast.error('Failed to load FX rate data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFxData();
  }, [fetchFxData]);

  const handleRefreshRate = async () => {
    setIsUpdating(true);
    try {
      const token = localStorage.getItem('vipgez_admin_token');
      const response = await fetch(`${API_BASE}/api/admin/fx-rate/refresh`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success(`FX rate updated: ${data.fxRate?.rate?.toFixed(6)} TRY/EUR`);
        fetchFxData();
      } else {
        toast.error(data.error || 'Failed to refresh rate');
      }
    } catch (error) {
      toast.error('Failed to refresh FX rate');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSetManualRate = async () => {
    const rate = parseFloat(manualRate);
    if (isNaN(rate) || rate <= 0) {
      toast.error('Please enter a valid rate');
      return;
    }

    setIsUpdating(true);
    try {
      const token = localStorage.getItem('vipgez_admin_token');
      const response = await fetch(`${API_BASE}/api/admin/fx-rate`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ rate, source: 'manual' })
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success('FX rate updated manually');
        fetchFxData();
      } else {
        toast.error(data.error || 'Failed to set rate');
      }
    } catch (error) {
      toast.error('Failed to set FX rate');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSaveSettings = async () => {
    setIsSavingSettings(true);
    try {
      const token = localStorage.getItem('vipgez_admin_token');
      const response = await fetch(`${API_BASE}/api/admin/fx-rate/settings`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success('FX settings saved');
      } else {
        toast.error(data.error || 'Failed to save settings');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleSettingsChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  // Calculate example conversions
  const rate = fxData?.rate || 0.027;
  const examples = [
    { try: 1000, eur: (1000 * rate).toFixed(2) },
    { try: 2000, eur: (2000 * rate).toFixed(2) },
    { try: 5000, eur: (5000 * rate).toFixed(2) },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">FX Rate Management</h1>
          <p className="text-gray-400 text-sm mt-1">
            Manage TRY to EUR exchange rate for Zuppin pricing
          </p>
        </div>
      </div>

      {/* Current Rate Card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-secondary/50 border-white/10 md:col-span-2">
          <CardHeader className="border-b border-white/10">
            <CardTitle className="text-white flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-orange-500" />
              Current Exchange Rate
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="flex items-center gap-8">
              <div>
                <p className="text-gray-400 text-sm">1 TRY equals</p>
                <p className="text-4xl font-bold text-orange-500">
                  €{rate.toFixed(6)}
                </p>
                <p className="text-gray-400 text-sm mt-1">
                  1 EUR = ₺{(1/rate).toFixed(2)}
                </p>
              </div>
              <div className="border-l border-white/10 pl-8">
                <p className="text-gray-400 text-sm">Last Updated</p>
                <p className="text-white font-medium">
                  {fxData?.updated_at 
                    ? new Date(fxData.updated_at).toLocaleString()
                    : 'Never'}
                </p>
                <p className="text-gray-500 text-sm">
                  Source: {fxData?.source || 'manual'}
                </p>
              </div>
              
              {schedulerStatus && schedulerStatus.next_update && settings.auto_update_enabled && (
                <div className="border-l border-white/10 pl-8">
                  <p className="text-gray-400 text-sm">Next Auto-Update</p>
                  <p className="text-white font-medium">
                    {new Date(schedulerStatus.next_update).toLocaleString()}
                  </p>
                  <p className={`text-sm ${schedulerStatus.running ? 'text-green-500' : 'text-yellow-500'}`}>
                    {schedulerStatus.running ? '● Scheduler Active' : '○ Scheduler Inactive'}
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-4 mt-6">
              <Button 
                onClick={handleRefreshRate} 
                disabled={isUpdating}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isUpdating ? 'animate-spin' : ''}`} />
                Fetch Live Rate
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Quick Conversion */}
        <Card className="bg-secondary/50 border-white/10">
          <CardHeader className="border-b border-white/10">
            <CardTitle className="text-white flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-orange-500" />
              Quick Conversion
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-3">
              {examples.map((ex, i) => (
                <div key={i} className="flex justify-between items-center py-2 border-b border-white/5">
                  <span className="text-gray-300">₺{ex.try.toLocaleString()}</span>
                  <span className="text-orange-500 font-medium">€{ex.eur}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Manual Rate Setting */}
      <Card className="bg-secondary/50 border-white/10">
        <CardHeader className="border-b border-white/10">
          <CardTitle className="text-white flex items-center gap-2">
            <Euro className="h-5 w-5 text-orange-500" />
            Set Manual Rate
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="flex gap-4 items-end">
            <div className="flex-1 max-w-xs">
              <label className="text-sm font-medium text-gray-300 block mb-1.5">
                TRY to EUR Rate
              </label>
              <Input
                type="number"
                step="0.000001"
                value={manualRate}
                onChange={(e) => setManualRate(e.target.value)}
                placeholder="0.027000"
                className="bg-black/30 border-white/20 text-white placeholder:text-gray-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                1 TRY = {manualRate || '0'} EUR
              </p>
            </div>
            <Button 
              onClick={handleSetManualRate}
              disabled={isUpdating}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
            >
              {isUpdating ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Set Rate
            </Button>
          </div>

          <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <div className="flex gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0" />
              <div>
                <p className="text-yellow-500 font-medium text-sm">Important</p>
                <p className="text-yellow-500/80 text-xs mt-1">
                  Manual rates override automatic updates. Make sure to set accurate rates to avoid pricing issues.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Settings */}
      <Card className="bg-secondary/50 border-white/10">
        <CardHeader className="border-b border-white/10">
          <CardTitle className="text-white flex items-center gap-2">
            <Settings className="h-5 w-5 text-orange-500" />
            Auto-Update Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.auto_update_enabled}
                  onChange={(e) => handleSettingsChange('auto_update_enabled', e.target.checked)}
                  className="rounded border-white/20 bg-black/30 text-orange-500 focus:ring-orange-500"
                />
                <span className="text-sm text-gray-300">Enable Auto-Update</span>
              </label>
              <p className="text-xs text-gray-500 mt-1">
                Automatically fetch rate from external API
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-300 block mb-1.5">
                Update Interval (hours)
              </label>
              <Input
                type="number"
                value={settings.update_interval_hours}
                onChange={(e) => handleSettingsChange('update_interval_hours', parseInt(e.target.value) || 24)}
                className="bg-black/30 border-white/20 text-white"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-300 block mb-1.5">
                Fallback Rate
              </label>
              <Input
                type="number"
                step="0.000001"
                value={settings.fallback_rate}
                onChange={(e) => handleSettingsChange('fallback_rate', parseFloat(e.target.value) || 0.027)}
                className="bg-black/30 border-white/20 text-white"
              />
              <p className="text-xs text-gray-500 mt-1">Used when API fails</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-300 block mb-1.5">
                Rate Range
              </label>
              <div className="flex gap-2 items-center">
                <Input
                  type="number"
                  step="0.001"
                  value={settings.min_rate}
                  onChange={(e) => handleSettingsChange('min_rate', parseFloat(e.target.value) || 0.02)}
                  placeholder="Min"
                  className="bg-black/30 border-white/20 text-white w-24"
                />
                <span className="text-gray-500">-</span>
                <Input
                  type="number"
                  step="0.001"
                  value={settings.max_rate}
                  onChange={(e) => handleSettingsChange('max_rate', parseFloat(e.target.value) || 0.04)}
                  placeholder="Max"
                  className="bg-black/30 border-white/20 text-white w-24"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Safety limits</p>
            </div>
          </div>

          <div className="mt-6">
            <Button 
              onClick={handleSaveSettings}
              disabled={isSavingSettings}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {isSavingSettings ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Save Settings
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminFxRatePage;
